/**
 * ImportWorkflowModal Component
 *
 * 2-step wizard to import a workflow (or bundle) previously exported via
 * workflowsApi.exportOne/exportAll:
 *   1. Upload — pick the exported .json file, validated + cross-referenced
 *      against the current tenant via workflowsApi.importPreview.
 *   2. Mapping — resolve any broken/ambiguous references (channel, funnel,
 *      funnelStep, pipeline, pipelineStage, agent, template, user, team,
 *      contact, database, form, checklistTemplate, unit) before confirming
 *      the import.
 *
 * Imported workflows are always created inactive — the confirm step just
 * kicks off the mutation and closes.
 */

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { WorkflowExportFile, WorkflowImportPreview } from '@troia-v3/types';
import { workflowsApi } from '../../services';
import { useImportWorkflow } from '../../hooks';
import { RefDropdown } from './RefDropdown';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentFolderId?: string | null;
  /**
   * Ajusta os textos do wizard — 'skill' quando aberto pela tela de Skills
   * (skills SÃO workflows `usageType: 'skill'`; o fluxo de import é o mesmo).
   */
  entity?: 'workflow' | 'skill';
}

/** Textos por entidade — mesmo wizard serve Workflows e Skills. */
const LABELS = {
  workflow: {
    title: 'Importar workflow',
    dropHint: 'Selecione um arquivo .json exportado de um workflow ou de todos os workflows',
    bundleUnit: 'workflow(s)',
    success: 'Workflow(s) importado(s) como inativo(s).',
    error: 'Falha ao importar workflow.',
  },
  skill: {
    title: 'Importar skill',
    dropHint: 'Selecione um arquivo .json exportado de uma skill ou de todas as skills',
    bundleUnit: 'skill(s)',
    success: 'Skill(s) importada(s) como inativa(s).',
    error: 'Falha ao importar skill.',
  },
} as const;

export function ImportWorkflowModal({
  isOpen, onClose, currentFolderId, entity = 'workflow',
}: Props): React.ReactElement | null {
  const labels = LABELS[entity];
  const [file, setFile] = useState<WorkflowExportFile | null>(null);
  const [preview, setPreview] = useState<WorkflowImportPreview | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [isParsing, setIsParsing] = useState(false);
  const importMut = useImportWorkflow();

  if (!isOpen) return null;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMappings({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;

    setIsParsing(true);
    try {
      const parsed = JSON.parse(await f.text()) as WorkflowExportFile;

      // Árvore do arquivo × tela: um arquivo SÓ de workflows na tela de Skills
      // (ou vice-versa) importaria itens invisíveis na tela atual com toast de
      // sucesso ("sucesso fantasma"). Bundle misto (backup completo antigo)
      // passa — cada item vai pra sua árvore. Entries sem usageType (arquivos
      // antigos) = 'automation'.
      const entries = Array.isArray(parsed.workflows) ? parsed.workflows : [];
      if (entries.length > 0) {
        const kinds = new Set(entries.map((w) => w.usageType ?? 'automation'));
        if (kinds.size === 1) {
          const fileEntity = kinds.has('skill') ? 'skill' : 'workflow';
          if (fileEntity !== entity) {
            toast.error(
              entity === 'skill'
                ? 'Este arquivo contém workflows — importe pela tela de Workflows.'
                : 'Este arquivo contém skills — importe pela tela de Skills.'
            );
            return;
          }
        }
      }

      const pv = await workflowsApi.importPreview(parsed);

      // Pré-preenche mappings com os refs já resolvidos no tenant atual
      const initial: Record<string, string> = {};
      for (const ref of pv.references) {
        if (ref.resolved) initial[ref.token] = ref.sourceId;
      }

      setFile(parsed);
      setPreview(pv);
      setMappings(initial);
    } catch {
      toast.error('Arquivo de importação inválido.');
    } finally {
      setIsParsing(false);
    }
  };

  const setMap = (token: string, id: string) => {
    setMappings((m) => {
      const next = { ...m, [token]: id };
      // Trocar o pai (funil/pipeline) invalida a etapa/estágio escolhido
      // dentro do anterior — senão o fluxo importado ficaria com a etapa de
      // um funil e o funil de outro.
      if (m[token] !== id) {
        for (const ref of preview?.references ?? []) {
          if (ref.dependsOn === token) delete next[ref.token];
        }
      }
      return next;
    });
  };

  const requiredMissing = (preview?.references ?? []).some((r) => r.required && !mappings[r.token]);

  const handleImport = async () => {
    if (!file) return;
    try {
      const result = await importMut.mutateAsync({
        file,
        mappings,
        targetFolderId: file.kind === 'workflow' ? (currentFolderId ?? null) : undefined,
      });
      toast.success(labels.success);
      // Warnings do backend (ex.: referências não mapeadas removidas) — o
      // usuário precisa saber quais nós revisar antes de ativar.
      for (const warning of result.warnings) toast.warning(warning, { duration: 10000 });
      reset();
      onClose();
    } catch {
      toast.error(labels.error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="lg">
      <div data-testid="import-workflow-modal">
        <ModalHeader onClose={handleClose}>
          <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
            {labels.title}
          </h2>
        </ModalHeader>
        <ModalContent>
          {!preview ? (
            <div className="space-y-4">
              <div
                className="rounded-lg border-2 border-dashed p-8 text-center"
                style={{ borderColor: '#e5e7eb' }}
              >
                <Icon name="upload" className="w-10 h-10 mx-auto mb-3" style={{ color: '#94a3b8' }} />
                <p className="text-sm mb-3" style={{ color: '#64748b' }}>
                  {labels.dropHint}
                </p>
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={handleFile}
                  disabled={isParsing}
                  className="block mx-auto text-sm"
                  data-testid="workflow-import-file"
                />
              </div>
              {isParsing && (
                <p className="text-sm text-center" style={{ color: '#64748b' }}>
                  Lendo arquivo...
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {preview.kind === 'bundle' && (
                <p className="text-sm" style={{ color: '#334155' }}>
                  {preview.folderCount} pasta(s), {preview.workflowCount} {labels.bundleUnit}.
                </p>
              )}
              {preview.warnings.length > 0 && (
                <div className="space-y-1">
                  {preview.warnings.map((w, i) => (
                    <p key={i} className="text-sm" style={{ color: '#d97706' }}>
                      {w}
                    </p>
                  ))}
                </div>
              )}
              {preview.references.length > 0 ? (
                <div className="space-y-3">
                  {preview.references.map((ref) => (
                    <RefDropdown
                      key={ref.token}
                      refItem={ref}
                      value={mappings[ref.token]}
                      parentValue={ref.dependsOn ? mappings[ref.dependsOn] : undefined}
                      onChange={(id) => setMap(ref.token, id)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm" style={{ color: '#64748b' }}>
                  Nenhuma referência externa para mapear.
                </p>
              )}
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={handleClose} data-testid="workflow-import-cancel">
            Cancelar
          </Button>
          {preview && (
            <Button
              type="button"
              onClick={handleImport}
              disabled={requiredMissing || importMut.isPending}
              data-testid="workflow-import-confirm"
            >
              {importMut.isPending ? 'Importando...' : 'Importar'}
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
}

export default ImportWorkflowModal;
